// Central SEO constants. Per-route metadata ends up here so the
// marketing copy has a single place to live.

export const SITE = {
  name: 'Terp Track',
  tagline: 'Dial in your relief',
  // Set via VITE_SITE_URL or fall back to the Vercel-provided host at
  // build time. Used for canonical + OG absolute URLs — social
  // scrapers reject relative ones.
  url: import.meta.env.VITE_SITE_URL ?? 'https://terptrack.uk',
  defaultDescription:
    'UK medical cannabis terpene intelligence. Track batch terpene profiles, vape temperatures, and symptom relief — anonymised, patient-owned.',
  defaultOgImage: '/og-default.png',
} as const;

export type SeoData = {
  title: string;
  description?: string;
  ogImage?: string;
  canonical?: string;
  noIndex?: boolean;
  // One or more schema.org JSON-LD objects to embed on the page.
  // Google reads JSON-LD anywhere in the document, so we emit these
  // inline rather than forcing them into <head>.
  jsonLd?: unknown | unknown[];
};

// ---------------------------------------------------------------------------
// JSON-LD builders — keeps schema.org shapes out of route components so the
// structure can be unit-tested in isolation later.
// ---------------------------------------------------------------------------

export function productJsonLd(input: {
  name: string;
  brand: string;
  category: string;
  canonical: string;
  description?: string | null;
  image?: string | null;
  avgStars?: number | null;
  ratingCount?: number | null;
}): unknown {
  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: input.name,
    brand: { '@type': 'Brand', name: input.brand },
    category: input.category,
    url: absoluteUrl(input.canonical),
  };
  if (input.description) base.description = input.description;
  if (input.image) base.image = absoluteUrl(input.image);
  if (input.avgStars != null && input.ratingCount && input.ratingCount > 0) {
    base.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: input.avgStars,
      reviewCount: input.ratingCount,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return base;
}

export function productWithReviewJsonLd(input: {
  productName: string;
  productBrand: string;
  productCategory: string;
  productCanonical: string;
  reviewTitle: string;
  reviewBody: string;
  reviewAuthor?: string | null;
  overall10: number;
  datePublished: string;
}): unknown {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: input.productName,
    brand: { '@type': 'Brand', name: input.productBrand },
    category: input.productCategory,
    url: absoluteUrl(input.productCanonical),
    review: {
      '@type': 'Review',
      name: input.reviewTitle,
      reviewBody: input.reviewBody,
      datePublished: input.datePublished,
      author: {
        '@type': 'Person',
        name: input.reviewAuthor ?? 'Anonymous patient',
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: input.overall10,
        bestRating: 10,
        worstRating: 1,
      },
    },
  };
}

export function blogPostingJsonLd(input: {
  title: string;
  excerpt?: string | null;
  author?: string | null;
  datePublished: string;
  dateModified?: string | null;
  canonical: string;
  image?: string | null;
}): unknown {
  const url = absoluteUrl(input.canonical);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.title,
    description: input.excerpt ?? undefined,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: {
      '@type': 'Person',
      name: input.author ?? SITE.name,
    },
    image: absoluteUrl(input.image ?? SITE.defaultOgImage),
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
}

export function fullTitle(pageTitle: string): string {
  return `${pageTitle} · ${SITE.name}`;
}

export function absoluteUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${SITE.url.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

// British long-form date with ordinal suffix ("23rd April 2026").
// Used on blog post bylines and anywhere else we want the friendlier
// UK reading style. Intl.DateTimeFormat gives us "23 April 2026" but
// not the "rd" suffix, so we format the day separately.
export function formatBritishDate(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const suffix =
    day >= 11 && day <= 13
      ? 'th'
      : day % 10 === 1
      ? 'st'
      : day % 10 === 2
      ? 'nd'
      : day % 10 === 3
      ? 'rd'
      : 'th';
  const month = d.toLocaleString('en-GB', { month: 'long' });
  return `${day}${suffix} ${month} ${d.getFullYear()}`;
}
